import {
  Box,
  Button,
  Flex,
  HStack,
  Link,
  Select,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import {
  CellContext,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  date as commonDate,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';

import {
  InvoiceCalculationComparisonFragmentGQLType,
  LoanRecordFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import { SearchBarInput } from 'packages/common-frontend/components/SearchAndCopyComponents';
import { ColumnDisplaySettingsButton } from 'packages/common-frontend/components/TanStack/ColumnReordering';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  createDefaultTableColumnConfiguration,
  useTableColumnPreferences,
} from 'packages/common-frontend/hooks/useTableColumnPreferences';
import {
  dollarNumberWithCommas,
  truncateUuid,
} from 'packages/common-frontend/utils';
import { InvoiceStatusBadge } from 'packages/invoices/ui/InvoiceStatusBadge';
import {
  invoiceHasCalculationMismatches,
  InvoiceWithComputedStatus,
} from 'packages/invoices/ui/utils';
import { useGetLoanRecordsByMetadataQuery } from 'packages/loans/requests/loans.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';

const InvoiceTableEmptyHeaderCell = ({
  label,
  justifyContent,
}: {
  label: string;
  justifyContent?: string;
}) => {
  return (
    <Th minH="150px" backgroundColor={colors.background[100]} p="16px">
      <Flex alignItems="center" justifyContent={justifyContent ?? undefined}>
        <Box mr="8px">
          <Icon icon="sort" />
        </Box>
        <Text color="black" fontSize="16px">
          {label}
        </Text>
      </Flex>
    </Th>
  );
};

const InvoicesTableEmptyHeader = () => {
  return (
    <Tr>
      <InvoiceTableEmptyHeaderCell label="Due Date" />
      <InvoiceTableEmptyHeaderCell label="Status" />
      <InvoiceTableEmptyHeaderCell label="Invoice ID" />
      <InvoiceTableEmptyHeaderCell label="Facility" />
      <InvoiceTableEmptyHeaderCell label="Loan ID" />
      <InvoiceTableEmptyHeaderCell label="Amount Due" justifyContent="end" />
      <InvoiceTableEmptyHeaderCell label="Created" />
    </Tr>
  );
};

const InvoicesTableSkeletonRow = () => {
  return (
    <Tr>
      {_.times(7, (idx) => {
        const cellKey = idx;
        return (
          <Td key={cellKey}>
            <Skeleton h="50px" />
          </Td>
        );
      })}
    </Tr>
  );
};

export const InvoicesTableSkeleton = () => {
  return (
    <>
      <Table borderWidth="1px" borderColor={colors.gray[200]} mt="1.5em">
        <Thead>
          <InvoicesTableEmptyHeader />
        </Thead>
        <Tbody>
          {_.times(8, (idx) => {
            const rowKey = idx;
            return <InvoicesTableSkeletonRow key={rowKey} />;
          })}
        </Tbody>
      </Table>
    </>
  );
};

export const InvoicesTableEmpty = () => {
  return (
    <>
      <Table borderWidth="1px" borderColor={colors.gray[200]} mt="1.5em">
        <Thead>
          <InvoicesTableEmptyHeader />
        </Thead>
        <Tbody>
          <Tr>
            <Td colSpan={7}>There are no invoices to display.</Td>
          </Tr>
        </Tbody>
      </Table>
    </>
  );
};

const InvoiceTableCell = ({
  cellContext,
  formatter,
}: {
  cellContext: CellContext<InvoiceWithComputedStatus, any>;
  formatter: (value: any) => string;
}) => {
  const value = cellContext.getValue();
  return (
    <Text color={colors.black[70]} py="8px">
      {formatter(value)}
    </Text>
  );
};

const COLUMN_DEFS: Array<
  ColumnDef<InvoiceWithComputedStatus, any> & { id: string }
> = [
  {
    id: 'dueDate',
    accessorKey: 'dueDate',
    header: 'Due Date',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) =>
          commonTypeguards.isFullDateStr(value)
            ? commonDate.fullDateStrToHumanDateString(value)
            : '-',
      }),
    minSize: 150,
  },
  {
    id: 'computedStatus',
    accessorKey: 'computedStatus',
    header: 'Status',
    cell: (cellContext) => {
      const StatusCell = () => {
        const computedStatus = cellContext.getValue();

        return <InvoiceStatusBadge computedStatus={computedStatus} />;
      };
      return StatusCell();
    },
    minSize: 150,
  },
  {
    id: 'id',
    accessorKey: 'id',
    header: 'Invoice ID',
    cell: function formatInvoiceId(cellContext) {
      const value = cellContext.getValue();
      return value ? (
        <Link as={RouterLink} to={`/invoices/${value}`}>
          <Typography
            colorScheme="blue"
            fontWeight="medium"
            variant="base-muted"
          >
            {truncateUuid(value)}
          </Typography>
        </Link>
      ) : (
        '-'
      );
    },
    minSize: 150,
  },
  {
    id: 'facility',
    accessorKey: 'creditFacility',
    header: 'Facility',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) => {
          const facilityIdParentheses = value?.id
            ? ` (${truncateUuid(value.id)})`
            : '';
          return value?.name ? `${value.name} ${facilityIdParentheses}` : '-';
        },
      }),
    minSize: 150,
  },
  {
    id: 'loanNames',
    accessorKey: 'loanNames',
    header: 'Loans',
    cell: function formatLoanNames({ getValue }) {
      const values = getValue();
      return (
        <Flex direction="column" gap="0.5rem">
          {values.slice(0, 2).map((loanName: string) => {
            return <Flex key={loanName}>{loanName}</Flex>;
          })}

          {values.length > 2 && (
            <Tooltip
              sx={{
                placement: 'left',
                bg: 'transparent',
                boxShadow: 'none',
              }}
              label={
                <Box
                  sx={{
                    p: '1rem',
                    rounded: 'lg',
                    backgroundColor: colors.aurora[500],
                  }}
                >
                  {values.slice(2).map((loanName: string) => (
                    <Flex key={loanName}>{loanName}</Flex>
                  ))}
                </Box>
              }
            >
              <Flex>
                <span style={{ borderBottom: '1px dotted gray' }}>
                  Other Loans
                </span>
              </Flex>
            </Tooltip>
          )}
          {values.length === 0 && '-'}
        </Flex>
      );
    },
    minSize: 150,
  },
  {
    id: 'loanIds',
    accessorKey: 'loanIds',
    header: 'Loan IDs',
    cell: function formatLoanIds({ getValue }) {
      const values = getValue();
      return (
        <Flex direction="column" gap="0.5rem">
          {values.slice(0, 2).map((loanId: string) => {
            return <Flex key={loanId}>{loanId}</Flex>;
          })}

          {values.length > 2 && (
            <Tooltip
              sx={{
                placement: 'left',
                bg: 'transparent',
                boxShadow: 'none',
              }}
              label={
                <Box
                  sx={{
                    p: '1rem',
                    rounded: 'lg',
                    backgroundColor: colors.aurora[500],
                  }}
                >
                  {values.slice(2).map((loanId: string) => {
                    return <Flex key={loanId}>{loanId}</Flex>;
                  })}
                </Box>
              }
            >
              <Flex>
                <span style={{ borderBottom: '1px dotted gray' }}>
                  Other Loans
                </span>
              </Flex>
            </Tooltip>
          )}
          {values.length === 0 && '-'}
        </Flex>
      );
    },
    minSize: 150,
  },
  {
    id: 'totalAmount',
    accessorKey: 'totalAmount',
    header: 'Amount Due',
    cell: function formatTotalAmountCell({ getValue, row }) {
      // invoiceCalculationComparisons may not be present when using lightweight fragments
      const comparison: InvoiceCalculationComparisonFragmentGQLType[] =
        row.getValue('invoiceCalculationComparisons') ?? [];
      const hasMismatches = invoiceHasCalculationMismatches(comparison);
      const totalAmountValue = getValue();

      return (
        <Tooltip
          label={hasMismatches ? 'Amount differs from Finley calculation' : ''}
        >
          <Flex alignItems="center" gap="0.5rem" justifyContent="flex-end ">
            {hasMismatches && (
              <Icon icon="exclamation-triangle" color={colors.orange[400]} />
            )}
            {Number.isFinite(totalAmountValue)
              ? `${dollarNumberWithCommas(totalAmountValue)}`
              : '-'}
          </Flex>
        </Tooltip>
      );
    },
    minSize: 150,
    meta: {
      style: {
        justifyContent: 'end',
        textAlign: 'right',
      },
    },
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) => {
          return commonDate.fullDateStrToHumanDateString(
            commonDate.epochToFullDateStr(value),
          );
        },
      }),
    minSize: 150,
  },
  // never show invoiceCalculationComparisons. only added to columnDef for metadata purposes
  {
    id: 'invoiceCalculationComparisons',
    accessorKey: 'invoiceCalculationComparisons',
  },
];

const TABLE_KEY = 'invoices.invoicesTable';
const hiddenColumns = ['invoiceCalculationComparisons'];

const DEFAULT_TABLE_CONFIG = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY,
  columns: COLUMN_DEFS.map((column) => ({
    columnKey: column.id,
    isVisible: !hiddenColumns.includes(column.id), // never show hidden columns. only added to columnDef for metadata purposes
    isFixed: false,
  })),
});

const columnNames = COLUMN_DEFS.reduce(
  (acc, column) => ({ ...acc, [column.id]: column.header }),
  {},
);

// Pagination controls component
type PaginationControlsProps = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

const PaginationControls = ({
  currentPage,
  totalPages,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  hasNextPage,
  hasPreviousPage,
}: PaginationControlsProps) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      py="1rem"
      px="0.5rem"
    >
      <Flex alignItems="center" gap="1rem">
        <Text color={colors.black[70]} fontSize="sm">
          Showing {startItem}-{endItem} of {totalCount} invoices
        </Text>
        <Flex alignItems="center" gap="0.5rem">
          <Text color={colors.black[70]} fontSize="sm">
            per page:
          </Text>
          <Select
            size="sm"
            width="80px"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
        </Flex>
      </Flex>

      <Flex alignItems="center" gap="0.5rem">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPageChange(1)}
          isDisabled={!hasPreviousPage}
        >
          First
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPageChange(currentPage - 1)}
          isDisabled={!hasPreviousPage}
        >
          Previous
        </Button>

        <Flex alignItems="center" gap="0.25rem" mx="0.5rem">
          {/* Show page numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_el, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <Button
                key={pageNum}
                size="sm"
                variant={currentPage === pageNum ? 'solid' : 'outline'}
                colorScheme={currentPage === pageNum ? 'blue' : 'gray'}
                onClick={() => onPageChange(pageNum)}
                minW="2.5rem"
              >
                {pageNum}
              </Button>
            );
          })}
        </Flex>

        <Button
          size="sm"
          variant="outline"
          onClick={() => onPageChange(currentPage + 1)}
          isDisabled={!hasNextPage}
        >
          Next
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onPageChange(totalPages)}
          isDisabled={!hasNextPage}
        >
          Last
        </Button>
      </Flex>
    </Flex>
  );
};

type InvoicesTableWithDataProps = {
  invoices: InvoiceWithComputedStatus[];
  loanRecords: LoanRecordFragmentGQLType[];
  // Pagination props
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  // Server-side sorting - when provided, sorting is handled by the server
  serverSorting?: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSortChange: (sortBy: string, sortDirection: 'asc' | 'desc') => void;
  };
};
const InvoicesTableWithData = ({
  invoices,
  loanRecords,
  pagination,
  serverSorting,
}: InvoicesTableWithDataProps) => {
  // When server-side sorting is provided, use controlled sorting state
  const [clientSorting, setClientSorting] = useState<SortingState>([
    { id: 'created_at', desc: true }, // default sorting created_at by desc so most recently created invoice is shown first
  ]);

  // Derive sorting state - use server sorting if provided, otherwise client sorting
  const sorting: SortingState = serverSorting
    ? [
        {
          id: serverSorting.sortBy,
          desc: serverSorting.sortDirection === 'desc',
        },
      ]
    : clientSorting;

  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const newSorting =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(sorting)
        : updaterOrValue;
    if (serverSorting && newSorting.length > 0) {
      // When server-side sorting is enabled, notify parent
      serverSorting.onSortChange(
        newSorting[0].id,
        newSorting[0].desc ? 'desc' : 'asc',
      );
    } else {
      // Fall back to client-side sorting
      setClientSorting(newSorting);
    }
  };

  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG,
  });

  const visibleColumnConfiguration = columnConfiguration.filter(
    (columnConfig) => !hiddenColumns.includes(columnConfig.columnKey),
  );

  const invoicesWithLoanNamesAndIds: (InvoiceWithComputedStatus & {
    loanNames: string[];
    loanIds: string[];
  })[] = useMemo(() => {
    return invoices.map((invoice) => {
      const loanNames: string[] = [];
      const loanIds: string[] = [];
      invoice.invoiceComponentsWithInvoiceItems.forEach((invoiceComponent) => {
        const loan = loanRecords.find(
          (loanRecord) =>
            loanRecord.id === invoiceComponent.invoiceComponent.finleyLoanId,
        );

        if (loan) {
          const externalLoanIdParentheses = loan.externalLoanId
            ? ` (${truncateUuid(loan.externalLoanId)})`
            : '';
          const formattedLoanId = loan.id
            ? `${truncateUuid(loan.id)} ${externalLoanIdParentheses}`
            : '-';

          loanNames.push(loan.name);
          loanIds.push(formattedLoanId);
        }
      });

      return { ...invoice, loanNames, loanIds };
    });
  }, [invoices, loanRecords]);

  const [searchQuery, setSearchQuery] = useState('');

  // When server-side pagination is enabled, search only filters the current visible page
  // This is a limited experience - for full search, the server would need to support it
  const filteredInvoices = useMemo(() => {
    const search = searchQuery.toLowerCase();
    if (!search) {
      return invoicesWithLoanNamesAndIds;
    }

    const isMatch = (input?: string | number | null): boolean => {
      if (!input) return false;
      if (typeof input === 'number') {
        return input.toString().includes(search);
      }
      return input.toLowerCase().includes(search);
    };

    return invoicesWithLoanNamesAndIds.filter((invoice) => {
      if (isMatch(invoice.status)) return true;
      if (isMatch(invoice.id)) return true;
      if (isMatch(invoice.creditFacility.name)) return true;
      if (isMatch(invoice.creditFacilityId)) return true;
      if (isMatch(JSON.stringify(invoice.loanNames))) return true;
      if (isMatch(JSON.stringify(invoice.loanIds))) return true;
      if (isMatch(invoice.totalAmount)) return true;
      return false;
    });
  }, [searchQuery, invoicesWithLoanNamesAndIds]);

  // When server-side sorting is enabled, don't use client-side sorted row model
  const table = useReactTable({
    data: filteredInvoices,
    columns: COLUMN_DEFS,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    // Only use client-side sorting when server-side sorting is not enabled
    getSortedRowModel: serverSorting ? undefined : getSortedRowModel(),
    manualSorting: !!serverSorting, // Enable manual sorting when server-side sorting is active
  });
  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <>
      <HStack gap="1em" width="100%" wrap="wrap" py="1.5rem">
        <Flex grow={1}>
          <SearchBarInput query={searchQuery} onChangeQuery={setSearchQuery} />
        </Flex>
        <ColumnDisplaySettingsButton
          initialColumns={visibleColumnConfiguration}
          updateColumnConfigs={updateUserColumnConfiguration}
          columnNames={columnNames}
        />
        <CsvXlsxDownloadSelect
          dataRows={csvDownload.dataRows}
          headers={csvDownload.headers}
          filenamePrefix="invoices"
        />
      </HStack>

      <Flex
        width="100%"
        overflowY="auto"
        borderWidth="1px"
        borderColor={colors.gray[200]}
      >
        <Table cursor="default">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnCanSort = header.column.getCanSort();
                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      position="sticky"
                      top="0"
                      borderBottomWidth="thin"
                      minW={`${header.column.columnDef.minSize}px`}
                      backgroundColor={colors.background[100]}
                    >
                      {header.isPlaceholder ? null : (
                        <Flex
                          alignItems="center"
                          justifyContent={
                            header.column.columnDef.meta?.style?.justifyContent
                          }
                        >
                          <Box
                            cursor={columnCanSort ? 'pointer' : 'default'}
                            onClick={header.column.getToggleSortingHandler()}
                            mr={columnCanSort ? '8px' : '0px'}
                          >
                            {{
                              asc: <Icon icon="sort-up" />,
                              desc: <Icon icon="sort-down" />,
                            }[header.column.getIsSorted() as string] ??
                              (columnCanSort ? <Icon icon="sort" /> : null)}
                          </Box>

                          <Box mr="8px">
                            <Text color="black" fontSize="16px" py="8px">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </Text>
                          </Box>
                        </Flex>
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          {filteredInvoices.length === 0 ? (
            <Tbody>
              <Tr>
                <Td colSpan={table.getAllColumns().length} padding="0">
                  <Flex
                    alignItems="center"
                    backgroundColor={colors.gray[20]}
                    minH="4rem"
                    pl="1.5rem"
                    width="100%"
                  >
                    <Typography>No entries found.</Typography>
                  </Flex>
                </Td>
              </Tr>
            </Tbody>
          ) : (
            <Tbody backgroundColor="white">
              {table.getRowModel().rows.map((row) => {
                return (
                  <Tr
                    key={row.id}
                    _hover={{ backgroundColor: colors.gray[50] }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        textAlign={cell.column.columnDef.meta?.style?.textAlign}
                        padding={
                          cell.column.columnDef.meta?.style?.padding ||
                          '8px 16px'
                        }
                        height="56px"
                        minW={`${cell.column.columnDef.minSize}px`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          )}
        </Table>
      </Flex>

      {pagination && pagination.totalCount > 0 && (
        <PaginationControls
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          totalCount={pagination.totalCount}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
          hasNextPage={pagination.hasNextPage}
          hasPreviousPage={pagination.hasPreviousPage}
        />
      )}
    </>
  );
};

type InvoicesTableProps = {
  invoices: InvoiceWithComputedStatus[] | undefined;
  isLoading: boolean;
  // Pagination props
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  // Server-side sorting props
  serverSorting?: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSortChange: (sortBy: string, sortDirection: 'asc' | 'desc') => void;
  };
};
export const InvoicesTable = ({
  invoices,
  isLoading,
  pagination,
  serverSorting,
}: InvoicesTableProps) => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = useMemo(
    () => facilities.map((facility) => facility.id),
    [facilities],
  );

  const {
    data: loanRecords,
    loading: isLoanRecordsLoading,
  } = useGetLoanRecordsByMetadataQuery({
    variables: {
      filterParams: {
        creditFacilityIds,
      },
    },
  });

  if (isLoading || !invoices || isLoanRecordsLoading || !loanRecords) {
    return <InvoicesTableSkeleton />;
  }

  if (!invoices.length) {
    return <InvoicesTableEmpty />;
  }

  return (
    <InvoicesTableWithData
      invoices={invoices}
      loanRecords={loanRecords.loans}
      pagination={pagination}
      serverSorting={serverSorting}
    />
  );
};
